import React, { createContext, useContext, useState, useEffect } from "react";

const EventContext = createContext();

export function useEventContext() {
  return useContext(EventContext);
}

export function EventProvider({ children }) {
  const [list, setList] = useState([]);
  const [priceDetails, setPriceDetails] = useState({});
  const [ticketData, setTicketData] = useState({});
  const [customItems, setCustomItems] = useState({});
  const [studentPrices, setStudentPrices] = useState({});
  const [generalAdmTicketTypeId, setGeneralAdmTicketTypeIds] = useState({});
  const [studentTicketTypeId, setStudentTicketTypeId] = useState({});
  const [ticketTypeNames, setTicketTypeNames] = useState({});
  const [eventInstances, setEventInstances] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [availableStatus, setAvailableStatus] = useState([]);

  const formatDate = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    const day = dateObj.toLocaleDateString("en-US", { weekday: "short" });
    const month = dateObj.toLocaleDateString("en-US", { month: "long" });
    const dateNumber = dateObj.getDate();
    const year = dateObj.getFullYear();
    const time = dateObj.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });

    return `${month} ${dateNumber} • ${time}`;
  };

  // Define the shared style properties for the tag labels
  const tagStyle = {
    padding: "6px", // Add padding to the container
    width: "auto",
    height: "5px",
    fontWeight: "normal", // Apply lighter font weight
    color: "#FFFFFF", // Set text color to white
    letterSpacing: "2px", // Increase letter-spacing to stretch out the text
    lineHeight: "0.1", // Adjust the height of the text
    fontSize: "0.6rem", // Adjust font size to make the text smaller
    background: "#2D2D2D", // Set the background color to white
    fontFamily: "Montserrat, Poppins, sans-serif",
    textTransform: "uppercase",
  };

  const getDayOfWeek = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj
      .toLocaleDateString("en-US", { weekday: "short" })
      .slice(0, 3);
  };

  const getDateNumber = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.getDate();
  };

  const getMonthName = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.toLocaleDateString("en-US", { month: "long" });
  };

  const getYearName = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.toLocaleDateString("en-US", { year: "numeric" });
  };

  const getTimeNumber = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });
  };

  //const [currentMonth, setCurrentMonth] = useState(() => getDate());

  // API Calls
  useEffect(() => {
    setLoading(true); // Set loading state to true before fetching data
    
    // Add a 2-second delay to see the loading state
    setTimeout(() => {
      fetch("https://events.jazzforumarts.org/jazzforumarts/api/v3/events")
        .then((response) => response.json())
        .then((data) => {
          setList(data);
          // Extract event IDs that have isOnSaleTrue
          const eventIds = data
            .filter((event) => event.isOnSale)
            .map((event) => event.id);
          // Trigger second API call to fetch event instances and instance IDs
          makeSecondAPICall(eventIds);
        })
        .catch((error) => {
          console.error("Error making first API call", error);
          setLoading(false); // Set loading state to false in case of error
        });
    }, 2000); // 2-second delay
  }, []);

  const makeSecondAPICall = (eventIds) => {
    Promise.all(
      eventIds.map((eventId) =>
        fetch(
          `https://events.jazzforumarts.org/jazzforumarts/api/v3/events/${eventId}/instances`
        ).then((response) => response.json())
      )
    )
      .then((responses) => {
        const updatedEventInstances = {};
        const instanceIds = {};

        responses.forEach((instanceDetails, index) => {
          if (Array.isArray(instanceDetails)) {
            const onSaleInstances = instanceDetails.filter(
              (event) => event.isOnSale && event.start
            );

            // Update event instances only if onSaleInstances is not empty
            if (onSaleInstances.length > 0) {
              updatedEventInstances[eventIds[index]] = onSaleInstances;
              instanceIds[eventIds[index]] = onSaleInstances.map(
                (instance) => instance.id
              );
            }
          }
        });
        setEventInstances(updatedEventInstances);
        makeThirdAPICall(instanceIds); // Trigger third API call with instance IDs
      })
      .catch((error) => {
        console.error("Error making second API call", error);
      });
  };

  const makeThirdAPICall = (instanceIds) => {
    // Create a copy of the current event prices state
    const updatedEventPrices = { ...priceDetails };
    const updatedCustomItems = { ...customItems }; // New state for customItems
    const updatedStudentPrices = { ...studentPrices }; // New state for studentPrices
    const updatedAdmTicketTypeIds = { ...generalAdmTicketTypeId }; // New object to hold ticket type IDs
    const updatedStuTicketTypeIds = { ...studentTicketTypeId }; // New object to hold ticket type IDs
    const updatedTicketTypeNames = { ...ticketTypeNames }; // New object to hold ticket type IDs
    const updatedTicketData = { ...ticketData };
    const updatedAvailableStatus = { ...availableStatus }; // New state for available status

    // Create an array to hold all the fetch promises
    const fetchPromises = [];

    // Iterate over instance IDs
    Object.keys(instanceIds).forEach((eventId) => {
      const ids = instanceIds[eventId];
      ids.forEach((instanceId) => {
        // Create a fetch promise for each instance
        const fetchPromise = fetch(
          `https://events.jazzforumarts.org/jazzforumarts/api/v3/instances/${instanceId}/price-list?$expand=prices/customItems`
        )
          .then((response) => response.json())
          .then((data) => {
            // Check if data.prices exists before accessing its properties
            if (data.prices && data.prices.length > 0) {
              // Find the student price
              const studentPriceData = data.prices.find(
                (price) =>
                  price.ticketType.name === "Student (must present ID at door)"
              );
              if (studentPriceData) {
                updatedStudentPrices[instanceId] = studentPriceData.amount; // Store student price for the instance
              }

              if (studentPriceData) {
                updatedStudentPrices[instanceId] = studentPriceData.amount; // Store student price for the instance
                const studentTicketTypeId = studentPriceData.ticketType.id;
                updatedStuTicketTypeIds[instanceId] = studentTicketTypeId;
                // You can do whatever you want with this ID, like storing it in state or elsewhere
              }

              // Find the standard price where isBandDefault is true
              const standardPrice = data.prices.find(
                (price) => price.isBandDefault === true
              );

              if (standardPrice) {
                const ticketTypeNames = standardPrice.ticketType.name;
                updatedTicketTypeNames[instanceId] = ticketTypeNames;
                // You can do whatever you want with this ID, like storing it in state or elsewhere
              }

              //console.log(ticketTypeNames);

              // Find the general admission ticket type
              const generalAdmissionPriceData = data.prices.find(
                (price) => price.ticketType.name === "General Adm"
              );
              if (generalAdmissionPriceData) {
                const generalAdmTicketTypeId =
                  generalAdmissionPriceData.ticketType.id;
                updatedAdmTicketTypeIds[instanceId] = generalAdmTicketTypeId;
                // You can do whatever you want with this ID, like storing it in state or elsewhere
              }

              if (standardPrice) {
                // Extracting custom items amounts
                const instanceCustomItems = standardPrice.customItems.map(
                  (customItem) => customItem.amount
                );

                // Update the event prices state with the standard price
                updatedEventPrices[instanceId] = standardPrice
                  ? standardPrice.amount
                  : null;
                // Update customItems state
                updatedCustomItems[instanceId] = instanceCustomItems;
              } else {
                console.error(
                  "No standard price found with isBandDefault: true"
                );
              }

              if (data.prices && data.prices.length > 0) {
                const eventData = data.prices.map((price) => ({
                  ticketTypeName: price.ticketType.name,
                  ticketTypeId: price.ticketType.id,
                  ticketAmount: price.amount,
                  commissionAmount: price.customItems[0]?.amount,
                }));

                // Further processing of prices if needed
                // Update the state with ticket type data for the current event
                //console.log(eventData);
                updatedTicketData[instanceId] = eventData;
              }
            }
          })
          .catch((error) => {
            console.error("Error making third API call", error);
          });

        // Second API call to retrieve availability status
        const fetchAvailabilityPromise = fetch(
          `https://events.jazzforumarts.org/jazzforumarts/api/v3/instances/${instanceId}/status?includeLockInformation=true&includeChildPlans=true`
        )
          .then((response) => response.json())
          .then((data) => {
            // Update available status state
            updatedAvailableStatus[instanceId] = data.available || 0;
          })
          .catch((error) => {
            console.error("Error making availability API call:", error);
            // Handle errors if needed
          });
        // Push the fetch promise to the array
        fetchPromises.push(fetchPromise);
        fetchPromises.push(fetchAvailabilityPromise);

        //setLoading(false);
      });
    });

    // Wait for all fetch promises to resolve
    Promise.all(fetchPromises).then(() => {
      // Once all fetch calls are completed, update the state
      setStudentPrices(updatedStudentPrices); // Update studentPrices state
      setPriceDetails(updatedEventPrices);
      setCustomItems(updatedCustomItems); // Update customItems state
      setGeneralAdmTicketTypeIds(updatedAdmTicketTypeIds); // Update ticketTypeIds state
      setStudentTicketTypeId(updatedStuTicketTypeIds);
      setTicketTypeNames(updatedTicketTypeNames);
      setTicketData(updatedTicketData);
      setAvailableStatus(updatedAvailableStatus); // Update available status state
      setLoading(false); // Move setLoading inside the .then() block
    });
  };

  // Initialize an array to store event instances and its event id
  const eventInstancesArray = Object.entries(eventInstances).map(
    ([eventId, instances]) => ({
      eventId,
      eventInstances: instances,
    })
  );

  // Initialize an empty array to store the merged data
  const mergedEventDetails = [];

  // Iterate over one of the arrays, eventInstancesArray in this case
  eventInstancesArray.forEach(({ eventId, eventInstances }) => {
    // Find the corresponding event in filteredEvents array using the Event ID
    const event = list.find((event) => event.id === eventId);

    // If the event is found, merge the data and push it to the mergedArray
    if (event) {
      const instancePrice =
        priceDetails[eventId] !== undefined ? priceDetails[eventId] : null;
      mergedEventDetails.push({
        event,
        eventInstances,
        priceDetails,
        customItems,
        studentPrices,
        generalAdmTicketTypeId,
        studentTicketTypeId,
        ticketTypeNames,
        ticketData,
        availableStatus,
      });
    }
  });

  // Sort the mergedEventDetails array based on eventInstances start times
  mergedEventDetails.sort((a, b) => {
    // Get the start times of the first instance for each event, if available
    const startTimeA =
      a.eventInstances.length > 0 ? new Date(a.eventInstances[0]?.start) : null;
    const startTimeB =
      b.eventInstances.length > 0 ? new Date(b.eventInstances[0]?.start) : null;

    // Handle cases where eventInstances array might be empty or undefined
    if (!startTimeA && !startTimeB) {
      return 0; // If both are null or undefined, consider them equal
    } else if (!startTimeA) {
      return 1; // If startTimeA is null or undefined, b should come first
    } else if (!startTimeB) {
      return -1; // If startTimeB is null or undefined, a should come first
    } else {
      return startTimeA - startTimeB; // Compare the valid start times and return the result
    }
  });

  function sortEventInstancesByDay(eventObject) {
    // Iterate over each event in the object
    for (let i = 0; i < eventObject.length; i++) {
      // Sort the eventInstances array by the start date
      eventObject[i].eventInstances.sort((a, b) => {
        const dateA = new Date(a.start);
        const dateB = new Date(b.start);
        return dateA - dateB;
      });
    }
    // Return the sorted event object
    return eventObject;
  }
  // Example usage:
  const sortedEventObject = sortEventInstancesByDay(mergedEventDetails);

  const transformedEventsTwo = sortedEventObject.map(
    ({
      event,
      eventInstances,
      priceDetails,
      customItems,
      studentPrices,
      studentTicketTypeId,
      generalAdmTicketTypeId,
      ticketTypeNames,
      ticketData,
      availableStatus,
    }) => {
      // Copy all properties of the event object
      const completeEventProperties = { ...eventInstances };

      // Add instances as a separate property
      completeEventProperties.eventDetails = event;

      // Add instances as a separate property
      completeEventProperties.priceDetails = priceDetails;

      // Add instances as a separate property
      completeEventProperties.customItems = customItems;

      // Add instances as a separate property
      completeEventProperties.studentPrices = studentPrices;

      // Add instances as a separate property
      completeEventProperties.studentTicketTypeId = studentTicketTypeId;

      // Add instances as a separate property
      completeEventProperties.generalAdmTicketTypeId = generalAdmTicketTypeId;

      // Add instances as a separate property
      completeEventProperties.ticketTypeNames = ticketTypeNames;

      // Add instances as a separate property
      completeEventProperties.ticketData = ticketData;

      // Add instances as a separate property
      completeEventProperties.availableStatus = availableStatus;

      // Return the new object
      return completeEventProperties;
    }
  );

  // Create an object to hold the grouped events
  const groupedEvents = {};

  Object.values(transformedEventsTwo).forEach((eventGroup) => {
    // Check if eventGroup is an array before using find
    if (Array.isArray(eventGroup)) {
      // Find the first event with a valid start property
      const eventWithStart = eventGroup.find((event) => event && event.start);
      
      if (eventWithStart) {
        const startDate = eventWithStart.start.substring(0, 10);
    
        if (groupedEvents[startDate]) {
          groupedEvents[startDate].push(...eventGroup);
        } else {
          groupedEvents[startDate] = [...eventGroup];
        }
      } else {
        console.warn("No event with a valid start time found in group", eventGroup);
      }
    } else {
      console.warn("Non-array event group encountered", eventGroup);
    }
  });

  const customEventDetails = [];

  // Iterate over the instances and log the start property along with eventDetails
  Object.values(transformedEventsTwo).forEach((instance) => {
    // Get the keys of the instance object
    const keys = Object.keys(instance);

    // Get the length of the keys array
    const length = keys.length;

    // Iterate over each key in the keys array
    keys.forEach((key, index) => {
      // Check if the current key is not the last one
      if (index !== length - 1) {
        // Add defensive checks for critical properties
        if (!instance[key] || !instance.eventDetails) {
          console.warn("Skipping event: missing key or event details", {
            instance,
            key,
          });
          return; // Skip this iteration
        }

        // Check for start time and isOnSale
        if (!instance[key].start || instance[key].isOnSale === undefined) {
          console.warn("Skipping event: missing start time or isOnSale", {
            start: instance[key].start,
            isOnSale: instance[key].isOnSale,
          });
          return; // Skip this iteration
        }

        // Extract the desired properties from each event object
        const customEvent = {
          start: instance[key].start,
          isOnSale: instance[key].isOnSale,
          planId: instance[key].planId || null,
          instanceId: instance[key].id || null,

          // Event details with fallback to empty string or null
          name: instance.eventDetails.name || "",
          id: instance.eventDetails.id || null,
          attribute_EventCategory:
            instance.eventDetails.attribute_EventCategory || "",
          firstInstanceDateTime:
            instance.eventDetails.firstInstanceDateTime || null,

          // Simplify repeated attribute checks
          attribute_EventTag1: instance.eventDetails.attribute_EventTag1 || "",
          attribute_EventTag2: instance.eventDetails.attribute_EventTag2 || "",
          attribute_EventTag3: instance.eventDetails.attribute_EventTag3 || "",

          // Use object spread to reduce repetition for attributes
          ...[
            "Feat1",
            "Feat2",
            "Feat3",
            "Feat4",
            "Feat5",
            "Feat6",
            "Feat7",
            "Feat8",
          ].reduce((acc, feat) => {
            acc[`attribute_${feat}`] =
              instance.eventDetails[`attribute_${feat}`] || "";
            return acc;
          }, {}),

          attribute_FeaturedArtists:
            instance.eventDetails.attribute_FeaturedArtists || "",
          attribute_FreeEvent:
            instance.eventDetails.attribute_FreeEvent || false,
          attribute_HideEvent:
            instance.eventDetails.attribute_HideEvent || false,
          attribute_MainArtist:
            instance.eventDetails.attribute_MainArtist || "",
          attribute_RSVPFacebookLink:
            instance.eventDetails.attribute_RSVPFacebookLink || "",
          attribute_SpecialInformation:
            instance.eventDetails.attribute_SpecialInformation || "",
          attribute_Subtitle: instance.eventDetails.attribute_Subtitle || "",
          attribute_VideoLink: instance.eventDetails.attribute_VideoLink || "",
          attribute_Website: instance.eventDetails.attribute_Website || "",

          description: instance.eventDetails.description || "",
          duration: instance.eventDetails.duration || null,
          lastInstanceDateTime:
            instance.eventDetails.lastInstanceDateTime || null,
          htmlDescription: instance.eventDetails.htmlDescription || "",
          imageUrl: instance.eventDetails.imageUrl || "",
          instanceDates: instance.eventDetails.instanceDates || [],
          thumbnailUrl: instance.eventDetails.thumbnailUrl || "",
          webEventId: instance.eventDetails.webEventId || null,

          // Pricing and ticket information with null checks
          prices: priceDetails[instance[key].id] || null,
          commission: customItems[instance[key].id] || null,
          studentPrice: studentPrices[instance[key].id] || null,
          studentTicketTypeId: studentTicketTypeId[instance[key].id] || null,
          generalTicketTypeId: generalAdmTicketTypeId[instance[key].id] || null,
          ticketTypeName: ticketTypeNames[instance[key].id] || "",
          ticketData: ticketData[instance[key].id] || null,
          availableStatus: availableStatus[instance[key].id] || 0,
        };

        // Push the custom event object into the custom array
        // Only push if the event meets minimum criteria
        if (customEvent.start && customEvent.name) {
          customEventDetails.push(customEvent);
        } else {
          console.warn("Skipping event: insufficient data", customEvent);
        }
      }
    });
  });

  return (
    <EventContext.Provider
      value={{
        list,
        setList,
        priceDetails,
        setPriceDetails,
        ticketData,
        setTicketData,
        customItems,
        setCustomItems,
        studentPrices,
        setStudentPrices,
        generalAdmTicketTypeId,
        setGeneralAdmTicketTypeIds,
        studentTicketTypeId,
        setStudentTicketTypeId,
        ticketTypeNames,
        setTicketTypeNames,
        availableStatus,
        setAvailableStatus,
        customEventDetails,
        eventInstances,
        loading,
      }}
    >
      {children}
    </EventContext.Provider>
  );
}
